import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useAuth } from './AuthContext';
import SignUpForm from './signupComponents/SignUpForm';
import { isRealToken } from './signupComponents/TurnstileWidget';
import { verifyAuthorizationCode } from './signupComponents/utils';
import { getCookie } from '../utils/cookieUtils';
// Stage-aware endpoint (src/config/api.js): npm start -> Development, builds -> Production.
import { USER_API_URL } from '../config/api';
import { joinClass } from '../StudentDashboardV3/data/api';
import BrandLockup from '../components/brand/BrandLockup';
import { authSwitchLink } from './authSwitchLink';
// The look (2026-10-28): the teacher signup's (survey.css), plus a few
// page-only rules in authLook.css. The page brings its own top bar; App.js
// gives /signup no site nav.
import '../Homepage/connect/sceneTokens.css';
import '../Homepage/connect/survey/survey.css';
import './authLook.css';

// Partner schools whose students must pick a grade at signup (mirrors the
// grade lists in SignUpForm.jsx).
const GRADE_REQUIRED_SCHOOLS = ['Erie', 'Jefferson', 'Bear Creek', 'Columbine', 'Chatfield', 'Clear Creek', 'Alamosa', 'Thornton'];

// Two messages on the code step are news, not errors: they show in the
// notice style. The words are the same as before.
const NEW_CODE_SENT = 'A new code was sent to your email.';
const RESUMED_SIGNUP = 'You already started signing up with this email. We sent a new verification code.';

function SignUp() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { isLoggedIn, user, login, logout } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingSignUp, setPendingSignUp] = useState(null); // formData held until email is verified
  const [verifyCode, setVerifyCode] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const plan = urlParams.get('plan');
  const redirect = urlParams.get('redirect');
  const source = urlParams.get('source');
  // The Log in button beside Sign up keeps redirect / plan / source.
  const signInLink = authSwitchLink('/signin', location.search);

  const clearErrorAfterDelay = () => {
    setTimeout(() => {
      setErrorMessage('');
    }, 5000);
  };

  async function finishSignIn(formData) {
    // Sign in after successful sign up / email verification
    const userInfo = await Auth.signIn(formData.username, formData.password);

    const idToken = userInfo.signInUserSession.idToken.jwtToken;
    const accessToken = userInfo.signInUserSession.accessToken.jwtToken;
    const refreshToken = userInfo.signInUserSession.refreshToken.token;

    await login({
      email: userInfo.attributes.email,
      username: userInfo.attributes.sub,
      Name: userInfo.attributes.name,
      fullName: userInfo.attributes['custom:FullName'],
      role: userInfo.attributes['custom:Role'],
      race: userInfo.attributes['custom:Race'],
      gender: userInfo.attributes['custom:Gender'],
      idToken,
      accessToken,
      refreshToken
    });

    // A class code from the teacher dashboard: joinClass makes the account
    // record (with the class's school) and the enrollment in one step.
    if (formData.v3JoinCode) {
      try {
        await joinClass({ user: { idToken, accessToken } }, formData.v3JoinCode);
      } catch (e) {
        console.warn('signup: joinClass failed', e?.status, e?.message);
      }
    }

    // New-stack account record (ClaudeUsers via claude-user-api): populates
    // role/name/school for the V3 dashboards. Fire-and-forget - signup never
    // blocks on it, and the lambda is idempotent (repeat calls fill gaps only).
    // Skipped after joinClass, which already made the record.
    if (!formData.v3JoinCode) try {
      fetch(USER_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body: JSON.stringify({
          action: 'onboardUser',
          school: userInfo.attributes['custom:School'] || formData.school || null,
          name: userInfo.attributes.name || formData.fullname || null,
          // Grade on the account record (Sandy 2026-08-09): the roster
          // editor shows it next to every name.
          grade: userInfo.attributes['custom:Grade'] || formData.grade || null,
          source: 'signup',
        }),
      }).catch(() => {});
    } catch (e) { /* never block signup on profile population */ }

    // Handle redirects based on plan and role
    if (plan === 'pro' || plan === 'practice') {
      const planType = plan === 'pro' ? 'pro' : 'practice';
      navigate(`/checkout?plan=${planType}&email=${formData.username}`);
    } else if (plan === 'premium') {
      navigate(`/checkout?plan=${plan}&email=${formData.username}`);
    } else if (formData.role === 'Teacher') {
      navigate('/teacher-dashboard');
    } else if (formData.role === 'Admin') {
      // Admins land on the V3 dashboard (signin does the same since
      // 2026-09-02; the 2.0 page stays at /admin-dashboard-legacy).
      navigate('/admin-dashboard');
    } else if (redirect) {
      navigate(redirect);
    } else {
      const currentDate = new Date().toISOString();
      // Never blocks the redirect (as on /signin): a failure here used to
      // leave a new student on "You're signed in" (Sandy 2026-10-29).
      try {
        await Auth.updateUserAttributes(userInfo, {
          'custom:LastLogin': currentDate
        });
      } catch (updateError) {
        console.error('Error updating last login:', updateError);
      }

      if (source === 'buff') {
        navigate('/diagnostic-mini-intro');
      } else {
        // Onboarding page removed (Sandy 2026-08-09): new students land on
        // the dashboard, whose diagnostic gate + tour take it from there.
        navigate('/student-dashboard');
      }
    }
  }

  async function handleSignUp(formData) {
    setIsSubmitting(true);
    setFormSubmitting(true);
    setErrorMessage('');

    try {
      // Validate mandatory fields
      if (!formData.username || !formData.password || !formData.confirmPassword || formData.signupType === null || (formData.signupType === 'partner' && !formData.fullname)) {
        setErrorMessage('Please fill in all mandatory fields');
        setIsSubmitting(false);
        clearErrorAfterDelay();
        return;
      }

      // Partner-school students pick a grade (Erie 2026-08-16, Clear Creek
      // 2026-08-08, Jefferson / Bear Creek / Columbine 2026-09-10); stored in
      // Cognito as custom:Grade at confirm, not editable afterwards. Students
      // join their teachers' classes with join codes after signup.
      if (GRADE_REQUIRED_SCHOOLS.includes(formData.school) && formData.signupType === 'partner' && formData.role === 'Student') {
        if (!formData.grade) {
          setErrorMessage('Please select your grade');
          setIsSubmitting(false);
          clearErrorAfterDelay();
          return;
        }
      }

      // Trim whitespace from email and authorization code
      formData.username = formData.username.trim();
      formData.authorizationCode = formData.authorizationCode ? formData.authorizationCode.trim() : '';

      // Ensure school and role are set correctly based on signup type
      if (formData.signupType === 'partner') {
        if (!formData.school || formData.school === 'No School') {
          setErrorMessage('Please select a school.');
          setIsSubmitting(false);
          clearErrorAfterDelay();
          return;
        }
        if (!formData.role) {
          setErrorMessage('Please select a role.');
          setIsSubmitting(false);
          clearErrorAfterDelay();
          return;
        }
        // Keep the school and role as selected by user
      } else {
        // Both teacher program and individual signup
        formData.school = 'No School';
        formData.role = 'Student';
        formData.grade = '';
        // "With a teacher" + a class code from the teacher dashboard
        // (2026-10-28, SignUpForm v3JoinCode): the class-code marker, so the
        // PreSignUp trigger checks the code and PostConfirmation gives the
        // student the class's school (same as /join).
        if (formData.v3JoinCode) formData.school = 'PENDING';
      }

      // Verify authorization code for partner schools
      if (formData.signupType === 'partner' && formData.school !== 'No School' && (formData.role === 'Teacher' || formData.role === 'Admin' || formData.role === 'Student')) {
        if (!formData.authorizationCode) {
          setErrorMessage('Authorization Code can not be left blank');
          setIsSubmitting(false);
          clearErrorAfterDelay();
          return;
        }
        // ALL schools verify server-side via the teacherAuthorizationCode
        // lambda (Sandy 2026-08-10) — the old Clear Creek frontend check
        // shipped its codes in the JS bundle, so it's gone; the lambda now
        // knows Clear Creek.
        try {
          const isAuthorized = await verifyAuthorizationCode(formData.authorizationCode, formData.school, formData.role, formData.username);
          if (!isAuthorized) {
            setErrorMessage('Invalid authorization code. Please check and try again.');
            setIsSubmitting(false);
            clearErrorAfterDelay();
            return;
          }
        } catch (authError) {
          setErrorMessage(authError.message);
          setIsSubmitting(false);
          clearErrorAfterDelay();
          return;
        }
      }

      // validationData is read by the PreSignUp lambda (autoConfirmUser):
      // - Turnstile token for the bot check. Real tokens only — the widget's
      //   fail-open sentinel must not reach Cognito.
      // - authorizationCode (2026-09-17): the partner-school code, re-checked
      //   SERVER-SIDE for Admin signups. The /authorizeTeacher call above is
      //   only the early, friendly check; it can be skipped by calling Cognito
      //   directly, this cannot.
      const validationData = {
        ...(isRealToken(formData.captchaToken) && { 'cf-turnstile-response': formData.captchaToken }),
        ...(formData.signupType === 'partner' && formData.authorizationCode && { authorizationCode: formData.authorizationCode }),
      };

      const signUpResponse = await Auth.signUp({
        username: formData.username,
        password: formData.password,
        attributes: {
          name: formData.fullname,
          'custom:HearAboutUs': formData.hearAboutUs,
          'custom:School': formData.school,
          'custom:Role': formData.role,
          'custom:Grade': formData.grade,
          'custom:Race': formData.race,
          'custom:Gender': formData.gender,
          // NEW: Add class code if present
          ...(formData.classCode && { 'custom:ClassCode': formData.v3JoinCode || formData.classCode }),
          ...(getCookie('affiliateId') && { 'custom:AffiliateId': getCookie('affiliateId') })
        },
        ...(Object.keys(validationData).length > 0 && { validationData }),
      });

      if (!signUpResponse.userConfirmed) {
        // Email verification required: show the code-entry step.
        // The account stays UNCONFIRMED (no sign-in)
        // until the code from the verification email is entered.
        setPendingSignUp(formData);
        return;
      }

      // Legacy path (account auto-confirmed): sign in directly
      await finishSignIn(formData);
    } catch (error) {
      console.error('Sign-up error:', error);
      if (error.code === 'UsernameExistsException') {
        // If the existing account never verified its email, resend a code
        // and drop the user into the verification step instead of failing.
        try {
          await Auth.resendSignUp(formData.username);
          setPendingSignUp(formData);
          setErrorMessage(RESUMED_SIGNUP);
        } catch (resendError) {
          setErrorMessage('An account with this email already exists. Please use a different email or try logging in.');
        }
      } else if (error.code === 'UserLambdaValidationException') {
        // Pre-signup lambda rejected the signup (invalid/disposable email domain).
        // Cognito prefixes the lambda's message; strip it for display.
        setErrorMessage(error.message.replace(/^PreSignUp failed with error /, '').trim() || 'Please enter a valid email address.');
      } else if (error.code === 'InvalidPasswordException') {
        setErrorMessage('Password does not meet the requirements. Please ensure it includes uppercase and lowercase letters, numbers, and special characters.');
      } else if (error.code === 'InvalidParameterException') {
        setErrorMessage('Invalid input. Please check all fields and try again.');
      } else {
        setErrorMessage('An error occurred during sign-up. Please try again later.');
      }
      clearErrorAfterDelay();
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleConfirmCode() {
    if (isConfirming) return;
    if (!/^\d{6}$/.test(verifyCode)) {
      setErrorMessage('Enter the 6-digit code from your email');
      clearErrorAfterDelay();
      return;
    }
    setIsConfirming(true);
    setErrorMessage('');
    try {
      await Auth.confirmSignUp(pendingSignUp.username, verifyCode);
      try {
        await finishSignIn(pendingSignUp);
      } catch (signInError) {
        // Account is verified but sign-in failed (e.g. re-signup with a
        // different password than the original attempt) — sign in manually.
        console.error('Post-verification sign-in error:', signInError);
        navigate('/signin');
      }
    } catch (error) {
      if (error.code === 'CodeMismatchException') {
        setErrorMessage('Incorrect code. Please try again.');
      } else if (error.code === 'ExpiredCodeException') {
        setErrorMessage('That code expired. Click "Resend code" to get a new one.');
      } else if (error.code === 'NotAuthorizedException') {
        // Already confirmed (e.g. double submit) — just sign in
        try {
          await finishSignIn(pendingSignUp);
          return;
        } catch (signInError) {
          navigate('/signin');
        }
      } else {
        setErrorMessage(error.message);
      }
      clearErrorAfterDelay();
    } finally {
      setIsConfirming(false);
    }
  }

  async function handleResendCode() {
    if (isResendingCode) return;
    setIsResendingCode(true);
    try {
      await Auth.resendSignUp(pendingSignUp.username);
      setErrorMessage(NEW_CODE_SENT);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsResendingCode(false);
    }
    clearErrorAfterDelay();
  }

  const handleSignOut = async () => {
    try {
      logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    setErrorMessage('');
  }, []);

  const view = isLoggedIn ? 'signed-in' : pendingSignUp ? 'verify' : 'form';
  const dashboardPath = user?.role === 'Admin' ? '/admin-dashboard'
    : user?.role === 'Teacher' ? '/teacher-dashboard'
    : '/student-dashboard';
  const isNotice = errorMessage === NEW_CODE_SENT || errorMessage === RESUMED_SIGNUP;

  return (
    // The teacher signup's look: a white page, its own top bar, one column.
    // "Log in" sits next to Sign up (2026-10-29), so the bar is the logo only.
    <div className="cx-root marketing-type ts-page al-page">
      <header className="ts-top">
        <Link to="/" className="ts-home" aria-label="Brain Buffs home">
          <BrandLockup />
        </Link>
      </header>
      <main className="ts-main">
        <div className="ts-form">
          <div key={view} className="ts-step">
            {view === 'signed-in' && (
              <>
                <h1 className="ts-title">You're signed in</h1>
                <p className="ts-sub">You are signed in with email: {user.email}</p>
                {/* The way back into the product (Sandy 2026-10-29): this page
                    has its own top bar, so the nav's "Go to dashboard" is gone.
                    Same role routes as NavigationBar's getDashboardRoute. */}
                <div className="ts-actions al-pair">
                  <Link to={dashboardPath} className="ts-btn">Go to my dashboard</Link>
                  <button type="button" className="ts-btn ts-btn--line" onClick={handleSignOut}>Sign out</button>
                </div>
              </>
            )}
            {view === 'verify' && (
              <>
                <h1 className="ts-title">Verify your email</h1>
                <p className="ts-sub">
                  We sent a 6-digit code to <strong>{pendingSignUp.username}</strong>.
                  Enter it below to finish creating your account.
                </p>
                <div className="ts-fieldset ts-form-fields">
                  <div className="ts-field">
                    <label className="ts-field-label" htmlFor="al-verify-code">6-digit code</label>
                    {/* Enter confirms, as before (there is no form here) */}
                    <input
                      id="al-verify-code"
                      type="text"
                      className="ts-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmCode(); }}
                      maxLength={6}
                    />
                  </div>
                  <p className="ts-quiet">
                    {"Didn't get it? Check your spam folder, or "}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isResendingCode}
                      className="ts-more ts-more--inline"
                    >
                      {isResendingCode ? 'sending...' : 'resend the code'}
                    </button>
                    .
                  </p>
                </div>
                {errorMessage && (
                  <p className={isNotice ? 'ts-notice' : 'ts-error'} role={isNotice ? 'status' : 'alert'}>{errorMessage}</p>
                )}
                <div className="ts-actions">
                  <button type="button" className="ts-btn" onClick={handleConfirmCode} disabled={isConfirming}>
                    {isConfirming ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              </>
            )}
            {view === 'form' && (
              <SignUpForm
                onSubmit={handleSignUp}
                isSubmitting={isSubmitting}
                formSubmitting={formSubmitting}
                errorMessage={errorMessage}
                signInLink={signInLink}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default SignUp;